/**
 * AI Provider Settings — operator picks which AI runs each capability.
 *
 * Top section: registered providers with a "configured" flag — green
 * pill if the API key is set (via the Integrations page), gray if not.
 *
 * Main section: one row per capability (text / json / vision / ...).
 * Each row is a provider dropdown + optional model override + save.
 * "Revert to default" deletes the DB row so the backend falls back to
 * the ``AI_PROVIDER`` env var (Claude if unset).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import aiSettingsService, {
  AICapability,
  AICapabilitySetting,
  AIProviderInfo,
} from '../services/aiSettingsService';

const CAPABILITY_LABELS: Record<AICapability, string> = {
  text: 'Text generation',
  json: 'Structured (JSON) output',
  vision: 'Image / vision',
  embeddings: 'Embeddings',
  transcription: 'Audio transcription',
};

const CAPABILITY_DESCRIPTIONS: Record<AICapability, string> = {
  text: 'Deal briefs, approach strategies, dossiers, and anywhere else free-form text is generated.',
  json: 'Structured outputs: match reviews ("Claude\'s Take"), expert agent analyses, extraction pipelines.',
  vision: 'Brochure and pitch-deck parsing — analyzing slide images to extract contact info and company details.',
  embeddings: 'Vector similarity search. Not currently used anywhere in the pipeline; set here for future features.',
  transcription: 'Speech-to-text for uploaded meeting audio and investor podcast / video discovery.',
};

const CAPABILITY_ORDER: AICapability[] = [
  'text',
  'json',
  'vision',
  'embeddings',
  'transcription',
];


type SaveState = 'idle' | 'saving' | 'clearing';


const AISettingsPage: React.FC = () => {
  const [providers, setProviders] = useState<AIProviderInfo[]>([]);
  const [settings, setSettings] = useState<AICapabilitySetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Per-row pending provider + model inputs (so the user can edit without
  // firing a request on every keystroke).
  const [draft, setDraft] = useState<
    Record<AICapability, { provider: string; model: string }>
  >({} as any);
  const [saveState, setSaveState] = useState<
    Record<AICapability, SaveState>
  >({} as any);

  const loadAll = async () => {
    setIsLoading(true);
    const [provRes, setRes] = await Promise.all([
      aiSettingsService.listProviders(),
      aiSettingsService.listSettings(),
    ]);
    if (provRes.error) toast.error(provRes.error);
    else if (provRes.data) setProviders(provRes.data);
    if (setRes.error) toast.error(setRes.error);
    else if (setRes.data) setSettings(setRes.data);
    setIsLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const settingByCapability = useMemo(() => {
    const map: Record<string, AICapabilitySetting | undefined> = {};
    settings.forEach((s) => {
      map[s.capability] = s;
    });
    return map;
  }, [settings]);

  const getDraft = (cap: AICapability) => {
    if (draft[cap]) return draft[cap];
    const current = settingByCapability[cap];
    return {
      provider: current?.provider_name ?? '',
      model: current?.model ?? '',
    };
  };

  const updateDraft = (
    cap: AICapability,
    patch: Partial<{ provider: string; model: string }>,
  ) => {
    setDraft((prev) => ({
      ...prev,
      [cap]: { ...getDraft(cap), ...patch },
    }));
  };

  const providersForCapability = (cap: AICapability): AIProviderInfo[] =>
    providers.filter((p) => p.capabilities.includes(cap));

  const handleSave = async (cap: AICapability) => {
    const d = getDraft(cap);
    if (!d.provider) {
      toast.error('Pick a provider first.');
      return;
    }
    setSaveState((prev) => ({ ...prev, [cap]: 'saving' }));
    const res = await aiSettingsService.upsertSetting(
      cap, d.provider, d.model || null,
    );
    setSaveState((prev) => ({ ...prev, [cap]: 'idle' }));
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`${CAPABILITY_LABELS[cap]} → ${d.provider}`);
    setDraft((prev) => {
      const next = { ...prev };
      delete next[cap];
      return next;
    });
    await loadAll();
  };

  const handleClear = async (cap: AICapability) => {
    if (!window.confirm(
      `Remove the override for ${CAPABILITY_LABELS[cap]}? ` +
      'It will revert to the system default (Claude).',
    )) return;
    setSaveState((prev) => ({ ...prev, [cap]: 'clearing' }));
    const res = await aiSettingsService.clearSetting(cap);
    setSaveState((prev) => ({ ...prev, [cap]: 'idle' }));
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`${CAPABILITY_LABELS[cap]} reverted to default`);
    setDraft((prev) => {
      const next = { ...prev };
      delete next[cap];
      return next;
    });
    await loadAll();
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-indigo-500" />
            AI Provider Settings
          </h1>
          <p className="text-gray-600 mt-2 leading-relaxed max-w-3xl">
            Choose which AI model powers each part of the engine.
            Anthropic (Claude) is the default. Add an API key on the{' '}
            <a href="/integrations" className="text-indigo-600 hover:underline">
              Integrations page
            </a>{' '}
            first, then point a capability at that provider here.
          </p>
        </div>

        {/* Providers overview */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Available providers
          </h2>
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {providers.map((p) => (
                <div
                  key={p.name}
                  className="border border-gray-200 rounded-lg p-4 bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{p.display_name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {p.default_model}
                      </div>
                    </div>
                    {p.configured ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-green-50 text-green-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Key set
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        No key
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.capabilities.map((cap) => (
                      <span
                        key={cap}
                        className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Per-capability assignments */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Capability assignments
          </h2>
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
            {CAPABILITY_ORDER.map((cap) => {
              const current = settingByCapability[cap];
              const d = getDraft(cap);
              const supported = providersForCapability(cap);
              const providerOptions = supported.length ? supported : providers;
              const saving = saveState[cap] === 'saving';
              const clearing = saveState[cap] === 'clearing';
              const isDirty =
                !!draft[cap] && (
                  d.provider !== (current?.provider_name ?? '') ||
                  d.model !== (current?.model ?? '')
                );
              return (
                <div key={cap} className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{CAPABILITY_LABELS[cap]}</div>
                      <div className="text-sm text-gray-600 mt-0.5 leading-relaxed">
                        {CAPABILITY_DESCRIPTIONS[cap]}
                      </div>
                      {!current && (
                        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                          <span className="px-1.5 py-0.5 bg-gray-100 rounded">Default</span>
                          <ArrowRight className="w-3 h-3" />
                          <span>Anthropic (Claude)</span>
                        </div>
                      )}
                      {current && (
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded font-medium">
                            {current.provider_name}
                          </span>
                          {current.model && (
                            <span className="ml-2 text-gray-500">{current.model}</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 w-80 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Provider
                        </label>
                        <select
                          value={d.provider}
                          onChange={(e) => updateDraft(cap, { provider: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">— choose —</option>
                          {providerOptions.map((p) => (
                            <option
                              key={p.name}
                              value={p.name}
                              disabled={!p.capabilities.includes(cap)}
                            >
                              {p.display_name}
                              {!p.capabilities.includes(cap) && ' (unsupported)'}
                              {!p.configured && ' (no key)'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Model override (optional)
                        </label>
                        <input
                          type="text"
                          value={d.model}
                          onChange={(e) => updateDraft(cap, { model: e.target.value })}
                          placeholder="leave blank for provider default"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => handleSave(cap)}
                          disabled={saving || clearing || !isDirty}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          Save
                        </button>
                        {current && (
                          <button
                            onClick={() => handleClear(cap)}
                            disabled={saving || clearing}
                            className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-60"
                            title="Revert to system default"
                          >
                            {clearing ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default AISettingsPage;
